import React, {useEffect, useMemo, useRef} from 'react';
import {ReactRenderer} from '@tiptap/react';
import {Mention as Extension} from '@tiptap/extension-mention';
import {SuggestionProps} from '@tiptap/suggestion';
import tippy, {GetReferenceClientRect, Instance} from 'tippy.js';
import {Menu, MenuItem, MenuItemTypography} from 'patient-ping-remedy/packages/menu';
import {User} from '@/api/dto/dto';
import TabsComponent, {TabsRef} from '@common/TabsComponent';
import {Wrapper} from '@common/messages/extensions/mention/Mention.styles';
import Avatar from '@common/Avatar';

type MentionProps = SuggestionProps<MentionItem, MentionItem> & {
  updatePopup(props: SuggestionProps): void;
}

export type MentionItem = MentionItemUser | MentionItemGroup;

type MentionItemUser = {
  id: string;
  label: string;
  user: User;
  category: MentionCategory.USER;
}

type MentionItemGroup = {
  id: number;
  label: string;
  category: MentionCategory.GROUP;
}

export enum MentionCategory {
  USER = 'user',
  GROUP = 'group',
}

const tabsIndex = {
  [MentionCategory.USER]: 0,
  [MentionCategory.GROUP]: 1,
};

const Mention = (props: MentionProps) => {
  const tabsRef = useRef<TabsRef>(null);
  const selectedTabIndex = useRef(0);

  const usersItems = useMemo(
    () => props.items.filter((item): item is MentionItemUser => item.category === MentionCategory.USER),
    [props.items],
  );

  const groupsItems = useMemo(
    () => props.items.filter((item): item is MentionItemGroup => item.category === MentionCategory.GROUP),
    [props.items],
  );

  const hidePopup = () => {
    props.decorationNode?.remove();
    props.editor.chain().focus(props.range.from);
  };

  const setCurrentTab = (index: number) => {
    selectedTabIndex.current = index;
    props.updatePopup(props);
  };

  const leftHandler = () => {
    const index = (selectedTabIndex.current + Object.keys(tabsIndex).length - 1) % Object.keys(tabsIndex).length;
    tabsRef.current?.changeTab(index);
    selectedTabIndex.current = index;
  };

  const rightHandler = () => {
    const index = (selectedTabIndex.current + 1) % Object.keys(tabsIndex).length;
    tabsRef.current?.changeTab(index);
    selectedTabIndex.current = index;
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      leftHandler();
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      rightHandler();
      event.preventDefault();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    selectedTabIndex.current = 0;
  }, [props.items]);

  return (
    <Wrapper id="mention">
      <TabsComponent
        ref={tabsRef}
        setCurrentTab={setCurrentTab}
        tabs={[
          {
            value: tabsIndex[MentionCategory.USER],
            label: 'Users',
            content: <Menu onHide={hidePopup}>
              {usersItems.map((item) => (
                <MenuItem key={item.id} onClick={() => props.command(item)}>
                  <Avatar user={item.user} />
                  <MenuItemTypography>{item.label}</MenuItemTypography>
                </MenuItem>
              ))}
            </Menu>,
          },
          {
            value: tabsIndex[MentionCategory.GROUP],
            label: 'Groups',
            content: <Menu>
              {groupsItems.map((item) => (
                <MenuItem key={item.id} onClick={() => props.command(item)}>
                  <MenuItemTypography>{item.label}</MenuItemTypography>
                </MenuItem>
              ))}
            </Menu>,
          },
        ]}
      />
    </Wrapper>
  );
};

export const ExtensionMention = Extension.configure({
  suggestion: {
    allowSpaces: true,
    render: () => {
      let component: ReactRenderer<never, MentionProps>;
      let popup: Instance[];

      return {
        onStart: (props) => {
          component = new ReactRenderer(Mention, {
            props: {
              ...props,
              updatePopup: (props: SuggestionProps) => {
                if (props.clientRect) {
                  popup[0].setProps({
                    getReferenceClientRect: props.clientRect as GetReferenceClientRect,
                  });
                }
              },
            },
            editor: props.editor,
          });

          if (props.clientRect) {
            popup = tippy('body', {
              getReferenceClientRect: props.clientRect as GetReferenceClientRect,
              appendTo: () => document.body,
              content: component.element,
              trigger: 'manual',
              interactive: true,
              showOnCreate: true,
              placement: 'bottom-start',
              popperOptions: {
                modifiers: [
                  {
                    name: 'flip',
                    enabled: false,
                  },
                ],
              },
            });
          }
        },
        onUpdate(props) {
          component.updateProps(props);
          component.props.updatePopup(props);
        },
        onExit() {
          if (!popup[0].state.isDestroyed) {
            popup[0].destroy();
          }

          component.destroy();
        },
      };
    },
  },
}).extend({
  addAttributes() {
    return {
      ...(Extension.config as any).addAttributes(),
      category: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-category'),
        renderHTML: (attributes) => {
          if (!attributes.category) {
            return {};
          }

          return {
            'data-category': attributes.category,
          };
        },
      }
    };
  },
});
